import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  verifyPasswordResetCode, 
  confirmPasswordReset,
  checkActionCode,
  applyActionCode
} from '../staffAuthApi';
import { app } from '../appAuth';
import { getParameterByName, sendPasswordResetEmail } from './emailUtils';
import { verifyEmailWithToken, sendCustomVerificationEmail } from '../api/emailVerificationApi';
import { VerifyEmailView } from '../verify-email/VerifyEmailView';
import '../verify-email/VerifyEmailView.css';
import { Box, Typography, Button } from '@mui/material';

const EmailActionHandler = ({ testMode = null, showTitle = true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [mode, setMode] = useState(null);
  const [actionCode, setActionCode] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth(app);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  const handleResendVerification = async () => {
    const user = auth.currentUser;
    if (!user) {
      setResendMessage({ type: 'error', text: 'Please sign in to resend the verification email.' });
      return;
    }
    try {
      setResendLoading(true);
      setResendMessage(null);
      const idToken = await user.getIdToken();
      await sendCustomVerificationEmail(idToken);
      setResendCooldownSeconds(120);
      setResendMessage({ type: 'success', text: 'Verification email sent. Check your inbox (and spam folder).' });
    } catch (err) {
      const sec = err?.retryAfterSeconds;
      if (typeof sec === 'number' && sec > 0) {
        setResendCooldownSeconds(sec);
        setResendMessage({ type: 'error', text: `Please wait ${sec} seconds before requesting another email.` });
      } else {
        setResendMessage({ type: 'error', text: err?.message || 'Failed to send. Try again in a minute.' });
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Function to extract parameters from the URL
  const extractUrlParameters = () => {
    let mode = getParameterByName('mode');
    let oobCode = getParameterByName('oobCode');
    let success = getParameterByName('success');
    const customToken = getParameterByName('token'); // Custom SendGrid verification link
    
    // If not found, try to extract from the full URL
    if (!mode || !oobCode) {
      const fullUrl = window.location.href;
      
      // Extract mode
      const modeMatch = fullUrl.match(/[?&]mode=([^&]*)/);
      if (modeMatch && modeMatch[1]) {
        mode = modeMatch[1];
      }
      
      // Extract oobCode
      const oobCodeMatch = fullUrl.match(/[?&]oobCode=([^&]*)/);
      if (oobCodeMatch && oobCodeMatch[1]) {
        oobCode = oobCodeMatch[1];
      }
      
      // Extract success
      const successMatch = fullUrl.match(/[?&]success=([^&]*)/);
      if (successMatch && successMatch[1]) {
        success = successMatch[1];
      }
    }

    return { mode, oobCode, success, customToken };
  };

  useEffect(() => {
    if (testMode) {
      // shortcut for UI previews without network calls
      setLoading(false);
      setError(null);
      setMode('verifyEmail');
      if (testMode === 'success') setSuccess('Your email has been verified successfully!');
      else if (testMode === 'message') setSuccess('Your email has been updated and verified!');
      else if (testMode === 'error') setError('Invalid or expired action code. Please try verifying your email again.');
      else if (testMode === 'loading') setLoading(true);
      return;
    }
    const handleEmailAction = async () => {
      try {
        const { mode: actionMode, oobCode, success, customToken } = extractUrlParameters();

        setMode(actionMode || 'verifyEmail');
        setActionCode(oobCode);

        if (customToken && (location.pathname.includes('reset-password') || actionMode === 'resetPassword')) {
          setMode('resetPassword');
          setActionCode(customToken);
          setLoading(false);
          return;
        }

        // Custom verification (SendGrid link with ?token=)
        if (customToken) {
          try {
            await verifyEmailWithToken(customToken);
            setSuccess('Your email has been verified successfully!');
          } catch (err) {
            setError(err?.message || 'Invalid or expired verification link. Please request a new verification email.');
          }
          setLoading(false);
          return;
        }

        // If success parameter is present, skip verification and show success message
        if (success === 'true' && (actionMode === 'verifyEmail' || actionMode === 'verifyAndChangeEmail')) {
          setSuccess(actionMode === 'verifyAndChangeEmail'
            ? 'Your email has been updated and verified!'
            : 'Your email has been verified successfully!');
          setLoading(false);
          return;
        }

        if (!oobCode) {
          // No link clicked: user was redirected here (e.g. unverified after invite). Show prompt + resend.
          setMode('verifyEmail');
          setLoading(false);
          setSuccess(null);
          setError(null);
          return;
        }

        if (!actionMode) {
          // Try to determine the mode from the pathname
          const pathname = location.pathname;
          if (pathname.includes('reset-password')) {
            setMode('resetPassword');
          } else if (pathname.includes('verify-email')) {
            setMode('verifyEmail');
          } else if (pathname.includes('recover-email')) {
            setMode('recoverEmail');
          } else {
            throw new Error('No action mode specified. Please check the link in your email and try again.');
          }
        }
        
        // Handle the user management action
        switch (actionMode || mode) {
          case 'resetPassword':
            // Verify the password reset code is valid
            try {
              const userEmail = await verifyPasswordResetCode(auth, oobCode);
              setEmail(userEmail);
              setLoading(false);
            } catch (verifyError) {
              console.error('Error verifying reset code:', verifyError);
              throw new Error(`Invalid or expired action code. Please request a new password reset email.`);
            }
            break;
            
          case 'recoverEmail':
            // Handle email recovery
            await handleRecoverEmail(oobCode);
            break;
            
          case 'verifyEmail':
            // Handle email verification
            await handleVerifyEmail(oobCode, continueUrl);
            break;
          case 'verifyAndChangeEmail':
            // Treat verifyAndChangeEmail same as verifyEmail for completion
            await handleVerifyEmail(oobCode, continueUrl);
            break;
            
          default:
            throw new Error(`Unsupported action mode: ${actionMode || mode}. Please check the link in your email and try again.`);
        }
      } catch (error) {
        console.error('Error handling email action:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    handleEmailAction();
  }, [location]);

  const handleRecoverEmail = async (actionCode) => {
    try {
      // Confirm the action code is valid
      const info = await checkActionCode(auth, actionCode);
      
      // Get the restored email address
      const restoredEmail = info.data.email;
      setEmail(restoredEmail);
      
      // Revert to the old email
      await applyActionCode(auth, actionCode);
      
      setSuccess(`Your email has been reverted to ${restoredEmail}`);
      setLoading(false);
    } catch (error) {
      setError('Invalid or expired action code. Please try again.');
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (actionCode, continueUrl) => {
    try {
      // Apply the email verification code
      await applyActionCode(auth, actionCode);
      
      setSuccess('Your email has been verified successfully!');
      setLoading(false);
      
      // If a continue URL is available, provide a button to redirect
      if (continueUrl) {
        setTimeout(() => {
          window.location.href = continueUrl;
        }, 3000);
      }
    } catch (error) {
      setError('Invalid or expired action code. Please try verifying your email again.');
      setLoading(false);
    }
  };

  const handleResetPassword = async (code) => {
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Resetting password with code:', code);
      // Reset the password
      await confirmPasswordReset(auth, code, newPassword);
      
      setSuccess('Your password has been reset successfully! You can now log in with your new password.');
      setPasswordError(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      setError(`Error resetting password: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    try {
      await sendPasswordResetEmail(email);
      setSuccess('Password reset email sent. Please check your inbox.');
    } catch (error) {
      setError('Error sending password reset email.');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleClose = () => {
    window.close();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <VerifyEmailView
          state="loading"
          copy="Processing your request…"
        />
      );
    }

    if (error) {
      return (
        <VerifyEmailView
          state="error"
          title="Something went wrong"
          copy={error}
          primaryActionLabel="Go to login"
          onPrimaryAction={() => navigate('/app/login')}
          secondaryActionLabel="Try again"
          onSecondaryAction={() => window.location.reload()}
        />
      );
    }

    if (success) {
      return (
        <VerifyEmailView
          state="success"
          title="Success!"
          copy={success}
          primaryActionLabel="Close window"
          onPrimaryAction={handleClose}
        />
      );
    }

    if (mode === 'verifyEmail' && !actionCode) {
      return (
        <VerifyEmailView
          state="prompt"
          title="Verify your email"
          copy="Please verify your email address to access the app. Check your inbox for the verification link."
          message={resendMessage?.text}
          messageType={resendMessage?.type}
          resendCooldownSeconds={resendCooldownSeconds}
          resendLoading={resendLoading}
          onResend={handleResendVerification}
          secondaryActionLabel="Back to login"
          onSecondaryAction={() => navigate('/app/login')}
        />
      );
    }

    if (mode === 'resetPassword') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Reset Your Password</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please enter a new password for your account: <strong>{email}</strong>
          </Typography>
          {passwordError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>{passwordError}</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              style={{ padding: '10px', width: '100%', maxWidth: 360, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{ padding: '10px', width: '100%', maxWidth: 360, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
            <Button size="small" onClick={handleTogglePasswordVisibility}>
              {showPassword ? 'Hide' : 'Show'}
            </Button>
            <Button variant="contained" onClick={() => handleResetPassword(actionCode)} disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Unknown action</Typography>
        <Button variant="contained" onClick={() => navigate('/app/login')}>Go to Login</Button>
      </Box>
    );
  };

  const getHeaderTitle = () => {
    switch (mode) {
      case 'resetPassword':
        return 'Reset Password';
      case 'verifyEmail':
        return 'Email Verification';
      case 'recoverEmail':
        return 'Email Recovery';
      default:
        return 'Account Action';
    }
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      {showTitle && (
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 700, mb: 1 }}>
          {getHeaderTitle()}
        </Typography>
      )}
      {renderContent()}
    </Box>
  );
};

export default EmailActionHandler; 